import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import authService from '../services/authService';

function Settings() {
  const user = authService.getCurrentUser();
  const { darkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('profile');

  // Profile form state
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    company: user?.company || ''
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileMessage, setProfileMessage] = useState({ type: '', text: '' });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileMessage({ type: '', text: '' });

    try {
      await authService.updateDetails(profileData);
      setProfileMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (err) {
      setProfileMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to update profile'
      });
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordMessage({ type: '', text: '' });

    if (passwordData.newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: 'New password must be at least 6 characters' });
      setPasswordLoading(false);
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      setPasswordLoading(false);
      return;
    }

    try {
      await authService.updatePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      setPasswordMessage({ type: 'success', text: 'Password updated successfully!' });
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      setPasswordMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to update password'
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field]
    });
  };

  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, text: '', color: '' };

    let strength = 0;
    if (password.length >= 6) strength++;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    if (strength <= 2) return { level: strength, text: 'Weak', color: '#ef4444' };
    if (strength <= 3) return { level: strength, text: 'Fair', color: '#f59e0b' };
    if (strength <= 4) return { level: strength, text: 'Good', color: '#10b981' };
    return { level: strength, text: 'Strong', color: '#059669' };
  };

  const passwordStrength = getPasswordStrength(passwordData.newPassword);

  const tabStyle = (isActive) => ({
    padding: '14px 36px',
    background: isActive
      ? 'linear-gradient(135deg, #059669 0%, #0891b2 100%)'
      : darkMode ? '#374151' : '#e5e7eb',
    color: isActive ? 'white' : darkMode ? '#e5e7eb' : '#374151',
    border: 'none',
    borderRadius: '10px 10px 0 0',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '1em',
    transition: 'all 0.2s ease',
    boxShadow: isActive ? '0 -2px 10px rgba(5, 150, 105, 0.3)' : 'none'
  });

  const cardStyle = {
    background: darkMode ? '#1e293b' : 'white',
    border: darkMode ? '1px solid #374151' : '1px solid #e5e7eb',
    borderRadius: '0 0 16px 16px',
    padding: '28px',
    width: '100%',
    boxShadow: darkMode ? '0 4px 20px rgba(0, 0, 0, 0.3)' : '0 4px 20px rgba(0, 0, 0, 0.08)'
  };

  const inputWrapperStyle = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  };

  const eyeButtonStyle = {
    position: 'absolute',
    right: '12px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    color: 'var(--text-secondary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const EyeIcon = ({ show }) => (
    show ? (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
        <line x1="1" y1="1" x2="23" y2="23"/>
      </svg>
    ) : (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
      </svg>
    )
  );

  return (
    <div className="app-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ width: '100%', maxWidth: '500px' }}>
        <div className="page-header" style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1>Settings</h1>
          <p style={{ color: 'var(--text-secondary)', marginTop: '8px' }}>
            Manage your account settings and preferences
          </p>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '4px',
          marginBottom: '0',
          justifyContent: 'center'
        }}>
        <button onClick={() => setActiveTab('profile')} style={tabStyle(activeTab === 'profile')}>
          Profile
        </button>
        <button onClick={() => setActiveTab('password')} style={tabStyle(activeTab === 'password')}>
          Password
        </button>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--text-primary)' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            Profile Information
          </h3>

          {profileMessage.text && (
            <div className={profileMessage.type === 'success' ? 'success-message' : 'error-message'}>
              {profileMessage.text}
            </div>
          )}

          <form onSubmit={handleProfileSubmit}>
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <input
                type="text"
                id="name"
                name="name"
                className="form-control"
                value={profileData.name}
                onChange={handleProfileChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                className="form-control"
                value={profileData.email}
                onChange={handleProfileChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="company">Company</label>
              <input
                type="text"
                id="company"
                name="company"
                className="form-control"
                value={profileData.company}
                onChange={handleProfileChange}
                placeholder="Enter your company name"
              />
            </div>

            <div style={{
              padding: '12px 16px',
              background: darkMode ? 'rgba(5, 150, 105, 0.15)' : 'rgba(5, 150, 105, 0.08)',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '0.9em',
              color: darkMode ? '#94a3b8' : '#64748b'
            }}>
              <strong>Role:</strong> {user?.role === 'admin' ? 'Administrator' : 'User'}
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={profileLoading}
              style={{ width: '100%', padding: '12px' }}
            >
              {profileLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </div>
      )}

      {/* Password Tab */}
      {activeTab === 'password' && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--text-primary)' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            Change Password
          </h3>

          {passwordMessage.text && (
            <div className={passwordMessage.type === 'success' ? 'success-message' : 'error-message'}>
              {passwordMessage.text}
            </div>
          )}

          <form onSubmit={handlePasswordSubmit}>
            <div className="form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <div style={inputWrapperStyle}>
                <input
                  type={showPasswords.current ? 'text' : 'password'}
                  id="currentPassword"
                  name="currentPassword"
                  className="form-control"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter your current password"
                  required
                  style={{ paddingRight: '44px' }}
                />
                <button type="button" onClick={() => togglePasswordVisibility('current')} style={eyeButtonStyle}>
                  <EyeIcon show={showPasswords.current} />
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <div style={inputWrapperStyle}>
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  id="newPassword"
                  name="newPassword"
                  className="form-control"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter your new password"
                  required
                  minLength={6}
                  style={{ paddingRight: '44px' }}
                />
                <button type="button" onClick={() => togglePasswordVisibility('new')} style={eyeButtonStyle}>
                  <EyeIcon show={showPasswords.new} />
                </button>
              </div>
              {passwordData.newPassword && (
                <div style={{ marginTop: '8px' }}>
                  <div style={{ display: 'flex', gap: '3px', marginBottom: '4px' }}>
                    {[1, 2, 3, 4, 5].map((level) => (
                      <div
                        key={level}
                        style={{
                          flex: 1,
                          height: '4px',
                          borderRadius: '2px',
                          background: level <= passwordStrength.level ? passwordStrength.color : (darkMode ? '#4b5563' : '#e5e7eb')
                        }}
                      />
                    ))}
                  </div>
                  <span style={{ fontSize: '0.8em', color: passwordStrength.color }}>{passwordStrength.text}</span>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <div style={inputWrapperStyle}>
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  className="form-control"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  placeholder="Confirm your new password"
                  required
                  style={{ paddingRight: '44px' }}
                />
                <button type="button" onClick={() => togglePasswordVisibility('confirm')} style={eyeButtonStyle}>
                  <EyeIcon show={showPasswords.confirm} />
                </button>
              </div>
              {passwordData.confirmPassword && (
                <div style={{ marginTop: '8px', fontSize: '0.8em' }}>
                  {passwordData.newPassword === passwordData.confirmPassword ? (
                    <span style={{ color: '#10b981' }}>Passwords match</span>
                  ) : (
                    <span style={{ color: '#ef4444' }}>Passwords do not match</span>
                  )}
                </div>
              )}
            </div>

            <div style={{
              padding: '12px 16px',
              background: darkMode ? 'rgba(5, 150, 105, 0.15)' : 'rgba(5, 150, 105, 0.08)',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '0.85em',
              color: darkMode ? '#94a3b8' : '#64748b'
            }}>
              <strong>Requirements:</strong> Minimum 6 characters. Use letters, numbers, and symbols for better security.
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={passwordLoading || (passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword)}
              style={{ width: '100%', padding: '12px' }}
            >
              {passwordLoading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>
      )}
      </div>
    </div>
  );
}

export default Settings;
